import type { MediaController as MediaControllerElement } from 'media-chrome'
import {
  MediaCaptionsMenu,
  MediaPlaybackRateMenu,
  MediaSettingsMenu,
  MediaSettingsMenuButton,
  MediaSettingsMenuItem
} from 'media-chrome/dist/react/menu'
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaPosterImage,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange
} from 'media-chrome/react'
import * as React from 'react'
import VimeoVideo from 'vimeo-video-element/react'
import YoutubeVideo from 'youtube-video-element/react'
import { pushDatalayer } from '../seo/datalayer'
import { parseMediaVideoFile } from '../utils/parseMedia'

export const Video = ({
  url,
  className,
  cookiebarElement,
  videoCookieAccepted,
  playerRef
}: {
  url: string
  videoCookieAccepted: boolean
  className?: string
  playerRef?: React.RefObject<MediaControllerElement>
  cookiebarElement?: JSX.Element
}) => {
  const { provider, id } = parseMediaVideoFile(url)
  const controllerRef = playerRef || React.useRef<MediaControllerElement>(null)
  const language = document.documentElement.lang || 'en'

  React.useEffect(() => {
    const controller = controllerRef.current
    const handlePlay = () => pushDatalayer({ action: 'Start', category: 'Video', label: url })
    const handlePause = () => pushDatalayer({ action: 'Pause', category: 'Video', label: url })

    if (controller && controller.media) {
      controller.media.addEventListener('play', handlePlay)
      controller.media.addEventListener('pause', handlePause)
    }
    return () => {
      const controller = controllerRef.current
      if (controller && controller.media) {
        controller.media.removeEventListener('play', handlePlay)
        controller.media.removeEventListener('pause', handlePause)
      }
    }
  }, [url])

  return !videoCookieAccepted && cookiebarElement && provider != 'html5' ? (
    <>{cookiebarElement}</>
  ) : (
    <MediaController
      ref={controllerRef}
      className={className}
      lang={language}
      style={{
        width: '100%',
        aspectRatio: '16 / 9'
      }}
    >
      {provider == 'html5' ? (
        <video slot="media" src={url} preload="auto" />
      ) : provider == 'vimeo' ? (
        <VimeoVideo
          src={`https://vimeo.com/${id}`}
          slot="media"
          playsInline
          // https://help.vimeo.com/hc/en-us/articles/12426260232977-About-Player-parameters
          config={{ cc: true, keyboard: false }}
        />
      ) : provider == 'youtube' ? (
        <>
          <YoutubeVideo
            src={`https://www.youtube.com/embed/${id}`}
            slot="media"
            playsInline
            // https://developers.google.com/youtube/player_parameters#Parameters
            config={{
              cc_load_policy: 1,
              cc_lang_pref: language,
              disablekb: 1,
              hl: language,
              origin: window.location.origin
            }}
          />
          <MediaPosterImage slot="poster" src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`} />
        </>
      ) : (
        <></>
      )}
      <div slot="centered-chrome">
        <MediaPlayButton />
      </div>

      <MediaControlBar className="bottom">
        <MediaPlayButton />
        <MediaTimeRange />
        <MediaTimeDisplay showDuration />
        <MediaMuteButton />
        <MediaVolumeRange />
        <MediaSettingsMenuButton />
        <MediaSettingsMenu hidden anchor="auto">
          <MediaSettingsMenuItem>
            Speed
            <MediaPlaybackRateMenu slot="submenu" hidden>
              <div slot="title">Speed</div>
            </MediaPlaybackRateMenu>
          </MediaSettingsMenuItem>
          <MediaSettingsMenuItem>
            Captions
            <MediaCaptionsMenu slot="submenu" hidden>
              <div slot="title">Captions</div>
            </MediaCaptionsMenu>
          </MediaSettingsMenuItem>
        </MediaSettingsMenu>
        <MediaFullscreenButton />
      </MediaControlBar>
    </MediaController>
  )
}
